import React, { useEffect, useState, useRef } from 'react';
import Typography from '../../../basic/Typography';
import { newNGApi } from '../services/newNGApi';
import styles from '../NewNG.module.css';

interface HeadlineTickerProps {
  jobId: string;
  networkName?: string;
  enhancedInfo?: any;
}

interface NewsEvent {
  headline: string;
  [key: string]: any;
}

export const HeadlineTicker: React.FC<HeadlineTickerProps> = ({ jobId, networkName, enhancedInfo }) => {
  const [headlines, setHeadlines] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [releaseTime, setReleaseTime] = useState<string>('');
  const [videoLength, setVideoLength] = useState<string>('');
  const [queryTopic, setQueryTopic] = useState<string>('');
  const [aggregationSource, setAggregationSource] = useState<string>('');
  const headlineRef = useRef<HTMLDivElement>(null);

  // Load headlines for the job
  useEffect(() => {
    if (!jobId) {
      setHeadlines([]);
      setIsLoading(false);
      return;
    }

    const loadHeadlines = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`/news/out/${jobId}/newsevent_stage/newsevent_stage_aggregation.json`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        let events: NewsEvent[] = [];

        if (Array.isArray(data.news_events)) {
          events = data.news_events;
        } else if (data.news_events && Array.isArray(data.news_events.events)) {
          events = data.news_events.events;
        }

        if (events.length > 0) {
          const headlineTexts = events
            .map(ev => ev.headline)
            .filter(Boolean);
          setHeadlines(headlineTexts);
        } else {
          setHeadlines(['No headlines available']);
        }
      } catch (err) {
        console.error('Error loading headlines:', err);
        setError('Failed to load headlines');
        // Fallback to sample headlines for testing
        setHeadlines([
          'Breaking: Major developments in global markets today',
          'Chancellor says NHS spending will rise by 3% a year after inflation',
          'New technology breakthrough announced in renewable energy sector',
          'Sports: Championship final set for next weekend',
          'Weather: Sunny skies expected across the country this week'
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadHeadlines();
  }, [jobId]);

  // Load job metadata for release time and video length (with resilience)
  useEffect(() => {
    if (!jobId) {
      setReleaseTime('');
      setVideoLength('');
      return;
    }

    let isMounted = true;

    const loadJobMetadata = async () => {
      try {
        // Use resilient API helper with 3 second timeout to not block UI
        const compositionData = await newNGApi.getCompositionOutput(jobId, 3000);

        // Check if component is still mounted before updating state
        if (!isMounted) return;

        // Extract and format release time from composition output
        if (compositionData?.completed_at) {
          try {
            const releaseDate = new Date(compositionData.completed_at);
            if (!isNaN(releaseDate.getTime())) {
              const timeString = releaseDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              });
              const dateString = releaseDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              });
              setReleaseTime(`${dateString} ${timeString}`);
            }
          } catch (dateErr) {
            console.debug('Invalid date in composition output');
          }
        }

        // Get duration from composition output (only if accurate data available)
        if (compositionData?.total_duration_sec && typeof compositionData.total_duration_sec === 'number' && compositionData.total_duration_sec > 0 && isMounted) {
          const duration = Math.round(compositionData.total_duration_sec);
          const minutes = Math.floor(duration / 60);
          const seconds = duration % 60;
          setVideoLength(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          // No duration available - don't show inaccurate data
          setVideoLength('');
        }
      } catch (err) {
        // Silent catch - don't break theatre mode if metadata fails
        console.debug('Job metadata unavailable:', err);
      }
    };

    loadJobMetadata();

    return () => {
      isMounted = false;
    };
  }, [jobId]);

  // Load newsevent metadata for topic and aggregation source (with resilience)
  useEffect(() => {
    if (!jobId) {
      setQueryTopic('');
      setAggregationSource('');
      return;
    }

    let isMounted = true;

    const loadNewseventMetadata = async () => {
      try {
        const newseventData = await newNGApi.getNewseventMetadata(jobId, 3000);

        if (!isMounted) return;

        if (newseventData?.query_topic) {
          setQueryTopic(newseventData.query_topic);
        }

        if (newseventData?.aggregation_source) {
          setAggregationSource(newseventData.aggregation_source);
        }
      } catch (err) {
        console.debug('Newsevent metadata unavailable:', err);
      }
    };

    loadNewseventMetadata();

    return () => {
      isMounted = false;
    };
  }, [jobId]);

  // Update current time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneAbbr = timezone.split('/').pop()?.replace('_', ' ') || timezone;
      setCurrentTime(`${timeString} ${timezoneAbbr}`);
    };

    // Update immediately
    updateTime();

    // Update every minute
    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  // Set ticker animation speed based on text width
  useEffect(() => {
    if (!headlineRef.current || headlines.length === 0) return;

    const setTickerSpeed = () => {
      const headline = headlineRef.current;
      if (!headline) return;

      const spans = headline.querySelectorAll('span > span');
      if (!spans.length) return;

      const span = spans[0] as HTMLElement;
      span.style.display = 'inline-block';

      const textWidth = span.offsetWidth;
      const containerWidth = headline.offsetWidth;

      // Simple constant speed (100px/sec)
      const speed = 100;
      const duration = (textWidth + containerWidth) / speed;

      spans.forEach(s => {
        (s as HTMLElement).style.animationDuration = `${duration}s`;
      });
    };

    // Wait for DOM update
    setTimeout(setTickerSpeed, 50);
  }, [headlines]);

  // Generate branding text with query topic and aggregation source
  const getBrandingText = () => {
    const displayName = networkName || 'News';

    // Prefer queryTopic from metadata API (accurate), fallback to enhancedInfo
    const topic = queryTopic || enhancedInfo?.metadata?.query?.topic;

    if (!topic) return displayName;

    // Format aggregation source for display
    const formatSource = (source: string) => {
      const sourceMap: Record<string, string> = {
        'perplexity': 'Perplexity',
        'xai': 'X.AI',
        'openrouter': 'OpenRouter'
      };
      return sourceMap[source.toLowerCase()] || source;
    };

    if (aggregationSource) {
      return `${displayName} - ${topic} (via ${formatSource(aggregationSource)})`;
    }

    return `${displayName} - ${topic}`;
  };

  // Build the right-side info string
  const buildInfoString = () => {
    const parts = [currentTime];
    if (releaseTime) parts.push(`Released: ${releaseTime}`);
    if (videoLength) parts.push(`Length: ${videoLength}`);
    return parts.join(' | ');
  };

  if (isLoading) {
    return (
      <div className={styles['newng-headline-ticker']}>
        <div className={styles['newng-headline-content']}>
          <Typography variant="news-ticker" component="span">Loading headlines...</Typography>
        </div>
        <div className={styles['newng-branding-bar']}>
          <Typography variant="news-logo" component="div" className={styles['newng-branding-logo']}>{getBrandingText()}</Typography>
          <Typography variant="news-time" component="div" className={styles['newng-branding-time']}>{buildInfoString()}</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['newng-headline-ticker']}>
        <div className={styles['newng-headline-content']}>
          <Typography variant="news-ticker" component="span">Error loading headlines</Typography>
        </div>
        <div className={styles['newng-branding-bar']}>
          <Typography variant="news-logo" component="div" className={styles['newng-branding-logo']}>{getBrandingText()}</Typography>
          <Typography variant="news-time" component="div" className={styles['newng-branding-time']}>{buildInfoString()}</Typography>
        </div>
      </div>
    );
  }

  const headlineText = headlines.join('  |  ');

  return (
    <div className={styles['newng-headline-ticker']}>
      {/* Red branding bar */}
      <div className={styles['newng-branding-bar']}>
        <Typography variant="news-logo" component="div" className={styles['newng-branding-logo']}>{getBrandingText()}</Typography>
          <Typography variant="news-time" component="div" className={styles['newng-branding-time']}>{buildInfoString()}</Typography>
      </div>

      {/* White headline ticker bar */}
      <div className={styles['newng-headline-content']} ref={headlineRef}>
        <span>
            <Typography variant="news-ticker" component="span">{headlineText}</Typography>
        </span>
      </div>
    </div>
  );
};
